(function () {
  "use strict";

  const VERSION = "0.12.1";
// Worker calls go through QB.transport (qb-known.js 0.7.0) when it is there: a store on a custom hostname
  // reaches its worker there, with a fallback to workers.dev on a failure. Plain fetch without it.
  const qbFetch = (url, init) => (window.QB?.transport?.fetch ? window.QB.transport.fetch(url, init) : fetch(url, init));

  // Two modes:
  //   "concierge" — qB-owned AI assistant backed by the Worker /chat endpoint
  //                 (default when a Worker URL is configured and no embedScript)
  //   "embed"     — legacy third-party provider widget (Gorgias, Intercom, …)
  const PROVIDER_SELECTORS = {
    gorgias: ["#gorgias-chat-container", ".gorgias-chat-widget"],
    intercom: ["#intercom-container", ".intercom-lightweight-app"],
  };

  const STORAGE_KEY = "qbChatSession";

  // Shopify Inbox as the concierge's human help (worker lib/help-config.js,
  // 26 Sep 2026). Shopify documents no way to open Inbox from a page, and its
  // markup has changed more than once, so Inbox is found by what it holds, not
  // by its name. Known shapes, newest first:
  //   2026: a Shopify web component whose open shadow root holds
  //         `button[part="activator"]` beside a `<shopify-chat-icon>` (seen on
  //         the review store, 27 Sep 2026)
  //   2024: #ShopifyChat, a shadow root with button[aria-controls="chat-ui"],
  //         .chat-toggle or .chat-app > button
  //   before: the launcher iframe #dummy-chat-button-iframe
  // openInbox() says whether one worked; the concierge shows its own form when
  // none did, so "Need a human?" never does nothing.
  const INBOX_BUTTON_SELECTORS = ['button[part="activator"]', 'button[aria-controls="chat-ui"]', ".chat-toggle", ".chat-app > button"];
  const INBOX_HIDE_STYLE_ID = "qb-inbox-hide";
  const INBOX_INNER_HIDE_ID = "qb-inbox-hide-inner";
  const INBOX_OPEN_CLASS = "qb-inbox-open";
  // Inbox loads after the page does: how long the hide waits for it to appear.
  const INBOX_WAIT_TRIES = 40;
  const INBOX_WAIT_MS = 500;
  let inboxOpenedThisVisit = false;

  function isInboxRoot(root) {
    return Boolean(root && root.querySelector && root.querySelector('button[part="activator"]') && root.querySelector("shopify-chat-icon"));
  }

  /** Inbox's host element: by its old names, else any element whose shadow root holds Inbox's launcher. */
  function findInboxHost(doc) {
    const named = doc.getElementById("ShopifyChat") || doc.querySelector("inbox-online-store-chat");
    if (named) return named;
    const all = doc.querySelectorAll ? doc.querySelectorAll("*") : [];
    for (const el of all) if (el.shadowRoot && isInboxRoot(el.shadowRoot)) return el;
    return null;
  }

  function inboxButton(doc) {
    const host = findInboxHost(doc);
    for (const root of [host && host.shadowRoot, host]) {
      if (!root) continue;
      for (const selector of INBOX_BUTTON_SELECTORS) {
        const button = root.querySelector(selector);
        if (button) return { button, host };
      }
    }
    try {
      const frame = doc.getElementById("dummy-chat-button-iframe");
      const button = frame && frame.contentWindow && frame.contentWindow.document.querySelector("button");
      if (button) return { button, host: null };
    } catch {
      /* a launcher on another origin: not ours to click */
    }
    return null;
  }

  // Today's Inbox keeps its launcher inside its own shadow root, where the
  // page's CSS cannot reach: the rule goes in beside it.
  function hideInboxLauncher(host, doc) {
    const root = host && host.shadowRoot;
    if (!root || root.querySelector(`#${INBOX_INNER_HIDE_ID}`)) return;
    const style = doc.createElement("style");
    style.id = INBOX_INNER_HIDE_ID;
    style.textContent = 'button[part="activator"] { display: none !important; }';
    root.appendChild(style);
  }

  function showInboxLauncher(host) {
    const style = host && host.shadowRoot && host.shadowRoot.querySelector(`#${INBOX_INNER_HIDE_ID}`);
    if (style) style.remove();
  }

  function openInbox(doc = document) {
    const found = inboxButton(doc);
    if (!found) return false;
    const { button, host } = found;
    // Shown for the rest of the visit once a shopper has asked for it.
    inboxOpenedThisVisit = true;
    for (const id of ["shopify-chat", "ShopifyChat"]) doc.getElementById(id)?.classList.add(INBOX_OPEN_CLASS);
    if (host && host.classList) host.classList.add(INBOX_OPEN_CLASS);
    showInboxLauncher(host);
    window.QB?.drawer?.close?.();
    try {
      // Already open: leave it open (the launcher toggles).
      if (button.getAttribute && button.getAttribute("aria-expanded") === "true") return true;
      button.click();
      return true;
    } catch {
      return false;
    }
  }

  // Inbox's own floating button, hidden until a shopper opens Inbox from the
  // concierge, when the store chose one way in (lib/help-config.js).
  function applyInboxButtonPolicy(help, doc = document, { wait = true } = {}) {
    const existing = doc.getElementById(INBOX_HIDE_STYLE_ID);
    const hide = Boolean(help && help.mode === "inbox" && help.hideInboxButton === true);
    if (!hide) {
      if (existing) existing.remove();
      showInboxLauncher(findInboxHost(doc));
      return;
    }
    if (!existing) {
      // The 2024 shape: a host the page's CSS can reach.
      const style = doc.createElement("style");
      style.id = INBOX_HIDE_STYLE_ID;
      style.textContent = `#shopify-chat:not(.${INBOX_OPEN_CLASS}), #ShopifyChat:not(.${INBOX_OPEN_CLASS}) { display: none !important; }`;
      doc.head.appendChild(style);
    }
    // Today's shape: inside the shadow root, once Inbox has loaded.
    let tries = 0;
    const attempt = () => {
      if (inboxOpenedThisVisit) return;
      const host = findInboxHost(doc);
      if (host && host.shadowRoot && isInboxRoot(host.shadowRoot)) {
        hideInboxLauncher(host, doc);
        return;
      }
      tries += 1;
      if (wait && tries < INBOX_WAIT_TRIES) setTimeout(attempt, INBOX_WAIT_MS);
    };
    attempt();
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  // Plain text with preserved line breaks. Since the concierge quotes the
  // store's own policies and pages (worker v0.34.0), two things more: **bold**
  // shows as bold rather than asterisks, and https links are clickable (a new
  // tab). Links are found in the raw text and every piece is escaped on its
  // own, so a quote or an angle bracket always ends a link and nothing the
  // model writes becomes any other markup; only https:// is ever linked.
  const REPLY_LINK = /https:\/\/[^\s<>"'`)\]]+[^\s<>"'`).,;:!?\]]/g;

  function formatInline(text) {
    return escapeHtml(text).replace(/\*\*([^*\n]{1,200})\*\*/g, "<strong>$1</strong>");
  }

  // The page the shopper is on, from the URL: /products/<handle> (also under a
  // collection or a market prefix such as /en-au), else /collections/<handle>.
  // The worker checks the handle again before it looks anything up.
  function pageRef() {
    try {
      const path = window.location.pathname;
      const product = path.match(/\/products\/([^/?#]+)/);
      const collection = path.match(/\/collections\/([^/?#]+)/);
      const pick = product ? ["product", product[1]] : collection && collection[1] !== "all" ? ["collection", collection[1]] : null;
      if (!pick) return undefined;
      const handle = decodeURIComponent(pick[1]).toLowerCase();
      return /^[a-z0-9][a-z0-9-]{0,254}$/.test(handle) ? { type: pick[0], handle } : undefined;
    } catch {
      return undefined;
    }
  }

  // Buttons under an answer (worker v0.35.1, plan WP10): only "switch to a
  // drawer tab" and only to a tab this drawer has, with a short plain label.
  // The worker adds them from the shopper's question, never the model.
  function cleanActions(actions) {
    if (!Array.isArray(actions)) return undefined;
    const tabs = (window.QB?.drawer?.slots || []).map((s) => s && s.key);
    const ok = actions
      .filter((a) => a && a.type === "tab" && typeof a.tab === "string" && tabs.includes(a.tab) && typeof a.label === "string" && a.label.length <= 40)
      .slice(0, 2)
      .map((a) => ({ type: "tab", tab: a.tab, label: a.label }));
    return ok.length ? ok : undefined;
  }

  // Product cards under an answer (worker v0.35.2, plan WP11): only the
  // fields a card shows, each checked: a numeric variant id for /cart/add.js,
  // an https link, an image from Shopify's CDN, a plain price and currency.
  function cleanCards(cards) {
    if (!Array.isArray(cards)) return undefined;
    const https = (u) => {
      try {
        return new URL(u).protocol === "https:" ? String(u) : null;
      } catch {
        return null;
      }
    };
    const ok = cards
      .filter((c) => c && typeof c.title === "string" && c.title && /^\d{1,20}$/.test(String(c.variantId)))
      .slice(0, 3)
      .map((c) => {
        const image = https(c.image);
        return {
          title: c.title.slice(0, 200),
          variant: typeof c.variant === "string" ? c.variant.slice(0, 100) : null,
          price: typeof c.price === "string" && /^\d+(\.\d+)?$/.test(c.price) ? c.price : null,
          currency: typeof c.currency === "string" && /^[A-Z]{3}$/.test(c.currency) ? c.currency : null,
          url: https(c.url),
          image: image && new URL(image).hostname === "cdn.shopify.com" ? image : null,
          variantId: String(c.variantId),
        };
      });
    return ok.length ? ok : undefined;
  }

  function cardHtml(c) {
    const meta = [c.variant, c.price ? `${c.currency ? `${c.currency} ` : ""}${c.price}` : null].filter(Boolean).join(" · ");
    const title = c.url ? `<a class="qb-chatbot__card-title" href="${escapeHtml(c.url)}">${escapeHtml(c.title)}</a>` : `<span class="qb-chatbot__card-title">${escapeHtml(c.title)}</span>`;
    return `<div class="qb-chatbot__card">${c.image ? `<img class="qb-chatbot__card-img" src="${escapeHtml(c.image)}" alt="" loading="lazy" width="56" height="56">` : ""}<div class="qb-chatbot__card-body">${title}${meta ? `<span class="qb-chatbot__card-meta">${escapeHtml(meta)}</span>` : ""}</div><button class="qb-chatbot__chip qb-chatbot__card-add" type="button" data-qb-add="${escapeHtml(c.variantId)}">Add to cart</button></div>`;
  }

  // The shopper's tap, and only that, adds one to the cart (Shopify's
  // /cart/add.js on the storefront's own origin).
  async function addToCart(button) {
    const id = Number(button.dataset.qbAdd);
    if (!Number.isSafeInteger(id) || id <= 0) return;
    button.disabled = true;
    button.textContent = "Adding…";
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    try {
      const res = await fetch(`${root}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: [{ id, quantity: 1 }] }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        button.textContent = typeof data.description === "string" && data.description.length < 120 ? data.description : "Couldn't add";
        return;
      }
      const link = document.createElement("a");
      link.className = button.className;
      link.href = `${root}cart`;
      link.textContent = "Added · View cart";
      button.replaceWith(link);
      document.dispatchEvent(new CustomEvent("qb:cart-added", { detail: { variantId: id } }));
    } catch {
      button.disabled = false;
      button.textContent = "Add to cart";
    }
  }

  function formatReply(str) {
    const raw = String(str ?? "");
    let out = "";
    let last = 0;
    for (const m of raw.matchAll(REPLY_LINK)) {
      out += formatInline(raw.slice(last, m.index));
      const url = escapeHtml(m[0]);
      out += `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
      last = m.index + m[0].length;
    }
    return (out + formatInline(raw.slice(last))).replace(/\n/g, "<br>");
  }

  function isValidScriptUrl(url) {
    try {
      const parsed = new URL(url);
      return parsed.protocol === "https:";
    } catch {
      return false;
    }
  }

  function hideNativeBubbles(provider, extraSelectors) {
    const selectors = [
      ...(PROVIDER_SELECTORS[provider] || []),
      ...(extraSelectors || []),
    ];
    if (!selectors.length) return;

    const styleId = "qb-chat-hide-native";
    if (document.getElementById(styleId)) return;

    const css = selectors.map((s) => `${s} { display: none !important; }`).join("\n");
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = css;
    document.head.appendChild(style);
  }

  async function fetchAuthToken(workerUrl, customer) {
    // Shared session token (qb-auth.js) — one mint per session for every slot.
    if (window.QB && typeof window.QB.token === "function") {
      const shared = await window.QB.token(customer);
      if (!shared) throw new Error("auth_failed");
      return shared;
    }
    const res = await qbFetch(`${workerUrl}/auth`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "token", customerId: customer.id, email: customer.email }),
    });
    const data = await res.json();
    if (!res.ok || !data.token) throw new Error(data.error || "auth_failed");
    return data.token;
  }

  // Who the saved thread belongs to: the signed-in customer's id, or null for
  // a guest. A different customer on the same tab (a shared device) starts
  // clean instead of seeing someone else's transcript (review, 23 Sep 2026).
  function sessionOwner() {
    const customer = window.QB?.customer;
    return customer?.loggedIn && customer.id ? String(customer.id) : null;
  }

  function loadSession() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed.messages)) return null;
      if (parsed.owner && parsed.owner !== sessionOwner()) {
        sessionStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }

  function saveSession(conversationId, messages, lastId, humanMode) {
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ conversationId, messages: messages.slice(-40), lastId, humanMode: Boolean(humanMode), owner: sessionOwner() })
      );
    } catch {
      /* storage unavailable — chat still works, just doesn't persist */
    }
  }

  class QBConciergeSlot {
    constructor(panel, config) {
      this.panel = panel;
      this.config = config;
      this.workerUrl = window.QB?.worker?.url || "";
      this.token = null;

      const session = loadSession();
      this.conversationId = session?.conversationId || null;
      this.messages = session?.messages || [];
      this.lastId = session?.lastId || 0;
      this.sending = false;
      this.error = null;
      this.escalateOpen = false;
      this.escalateDone = false;
      this.pollingSupported = true;
      // Gorgias handoff: true while the store team owns this thread (an open
      // ticket). Set from /chat, /chat/escalate and every poll; the poll is
      // how the drawer learns the ticket closed and the AI is back.
      this.humanMode = Boolean(session?.humanMode);

      this.renderShell();
      this.bind();
      this.startPolling();

      // Merchant copy may land after the first paint — refresh the intro
      // bubble once it does (only matters before any real messages exist).
      window.QB?.messagesReady?.then(() => {
        if (!this.messages.length && !this.sending) this.renderThread();
      });
    }

    // Human replies from the merchant console land in D1; polling brings them
    // into the open thread so an agent can genuinely take over mid-chat.
    startPolling() {
      if (this.pollTimer) return;
      this.pollTimer = setInterval(() => this.poll(), 10000);
    }

    async poll() {
      if (
        !this.pollingSupported ||
        !this.conversationId ||
        !this.workerUrl ||
        this.sending ||
        document.hidden
      ) {
        return;
      }
      try {
        // A thread a signed-in customer started answers only with their
        // token (SECURITY_BACKLOG #9, worker v0.19.0). Only a token already
        // held rides the poll: the poll never mints, or a failing mint would
        // hit /auth every 10 seconds (review, 23 Sep 2026).
        // After a page change this.token is empty until the next send, so the
        // session's cached token (qb-auth 0.2.0 tokenPeek) rides along too.
        const customer = window.QB?.customer;
        const held = customer?.loggedIn ? this.token || window.QB?.tokenPeek?.(customer) || null : null;
        const headers = held ? { Authorization: `Bearer ${held}` } : {};
        const res = await qbFetch(
          `${this.workerUrl}/chat/messages?conversationId=${encodeURIComponent(this.conversationId)}&after=${this.lastId}`,
          { headers }
        );
        if (res.status === 410) {
          // The thread was erased (a privacy request): start fresh.
          this.startNewConversation();
          this.renderThread();
          return;
        }
        if (res.status === 403) {
          const refused = await res.json().catch(() => ({}));
          if (refused.error === "conversation_bound") {
            if (!window.QB?.customer?.loggedIn) {
              // Signed out since: the thread is not this visitor's any more.
              this.startNewConversation();
              this.renderThread();
            } else if (this.token) {
              // The held token was refused (a rotated secret): drop it; the
              // next message mints afresh and recoverBoundConversation decides.
              window.QB?.tokenClear?.(window.QB.customer);
              this.token = null;
            }
            // Signed in with no token to hand: keep the thread and wait.
          }
          return;
        }
        if (!res.ok) return;
        const data = await res.json();
        if (data.transcripts === false) {
          this.pollingSupported = false; // Worker has no D1 — nothing to poll
          return;
        }
        let changed = false;
        for (const m of data.messages || []) {
          if (m.role === "agent") {
            this.messages.push({ role: "agent", content: m.content });
            changed = true;
          }
        }
        this.lastId = data.lastId ?? this.lastId;
        if (typeof data.mode === "string" && this.setHumanMode(data.mode === "human")) {
          changed = true;
        }
        if (changed) {
          saveSession(this.conversationId, this.messages, this.lastId, this.humanMode);
          this.renderThread();
        }
      } catch {
        /* transient network failure — next tick retries */
      }
    }

    // SECURITY_BACKLOG #9: the worker answers 403 "conversation_bound" for a
    // thread a signed-in customer wrote in, unless their token comes with the
    // call. A stale token gets one fresh mint; otherwise (signed out since, or
    // no token to be had) the next message starts a new conversation.
    async recoverBoundConversation(res, retry, { keepLast = false } = {}) {
      // An erased thread (410): nothing to recover — a new conversation.
      if (res.status === 410) {
        this.startNewConversation({ keepLast });
        return retry();
      }
      if (res.status !== 403) return res;
      const refused = await res.clone().json().catch(() => ({}));
      if (refused.error !== "conversation_bound") return res;
      const customer = window.QB?.customer;
      if (customer?.loggedIn) {
        // Signed in but no token could be minted just now: keep the thread
        // (and any open ticket) and let the error show — never abandon a
        // customer's own conversation over a transient failure.
        if (!this.token) return res;
        // The token held was refused: one fresh mint.
        window.QB?.tokenClear?.(customer);
        this.token = null;
        const again = await retry();
        if (again.status !== 403 || !this.token) return again;
        const still = await again.clone().json().catch(() => ({}));
        if (still.error !== "conversation_bound") return again;
        // A fresh token of THIS customer is refused: the thread is someone else's.
      }
      this.startNewConversation({ keepLast });
      return retry();
    }

    // Abandons the thread — and its transcript, so the previous owner's
    // messages never stay on screen or ride along as `history`. `keepLast`
    // keeps the message being sent right now.
    startNewConversation({ keepLast = false } = {}) {
      this.conversationId = null;
      this.lastId = 0;
      this.setHumanMode(false);
      this.messages = keepLast ? this.messages.slice(-1) : [];
      saveSession(this.conversationId, this.messages, this.lastId, this.humanMode);
    }

    // Returns true when the mode actually flipped so callers can re-render.
    setHumanMode(on) {
      const next = Boolean(on);
      if (next === this.humanMode) return false;
      this.humanMode = next;
      if (next) this.escalateOpen = false;
      return true;
    }

    // Personalized answers (points, tier) and a trusted customer id on the
    // escalation form need a verified token; guests still get full
    // knowledge-base answers without one.
    async authHeaders() {
      const customer = window.QB?.customer;
      const headers = { "Content-Type": "application/json" };
      if (customer?.loggedIn && customer.id && customer.email) {
        try {
          if (!this.token) this.token = await fetchAuthToken(this.workerUrl, customer);
          headers.Authorization = `Bearer ${this.token}`;
        } catch {
          this.token = null;
        }
      }
      return headers;
    }

    intro() {
      const customer = window.QB?.customer;
      const name = customer?.loggedIn && customer.firstName ? ` ${escapeHtml(customer.firstName)}` : "";
      const configured =
        this.config.intro ||
        (window.QB?.msg ? window.QB.msg("chat_intro", "") : "");
      return (
        configured ||
        `Hi${name}! I'm the store assistant. Ask me about products, shipping, returns, or your rewards.`
      );
    }

    suggested() {
      const chips = Array.isArray(this.config.suggested) ? this.config.suggested : [];
      return chips.slice(0, 4);
    }

    renderShell() {
      this.panel.innerHTML = `
        <div class="qb-chatbot">
          <div class="qb-chatbot__thread" data-qb-thread aria-live="polite"></div>
          <div class="qb-chatbot__escalate" data-qb-escalate-box hidden></div>
          <p class="qb-chatbot__error" data-qb-chat-error role="alert" hidden></p>
          <div class="qb-chatbot__composer">
            <textarea
              class="qb-chatbot__input"
              data-qb-chat-input
              rows="1"
              maxlength="1500"
              placeholder="Type a message…"
              aria-label="Chat message"
            ></textarea>
            <button class="qb-chatbot__send" type="button" data-qb-chat-send aria-label="Send message">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            </button>
          </div>
          <button class="qb-chatbot__human" type="button" data-qb-escalate-toggle>Need a human?</button>
        </div>
      `;
      this.threadEl = this.panel.querySelector("[data-qb-thread]");
      this.inputEl = this.panel.querySelector("[data-qb-chat-input]");
      this.renderThread();
    }

    renderThread() {
      const parts = [];

      parts.push(`<div class="qb-chatbot__msg qb-chatbot__msg--bot">${formatReply(this.intro())}</div>`);

      if (!this.messages.length && this.suggested().length) {
        parts.push(`
          <div class="qb-chatbot__chips">
            ${this.suggested()
              .map((q) => `<button class="qb-chatbot__chip" type="button" data-qb-chip>${escapeHtml(q)}</button>`)
              .join("")}
          </div>
        `);
      }

      for (const msg of this.messages) {
        if (msg.role === "agent") {
          parts.push(
            `<div class="qb-chatbot__msg qb-chatbot__msg--agent"><span class="qb-chatbot__agent-label">Store team</span>${formatReply(msg.content)}</div>`
          );
          continue;
        }
        const cls = msg.role === "user" ? "qb-chatbot__msg--user" : "qb-chatbot__msg--bot";
        parts.push(`<div class="qb-chatbot__msg ${cls}">${formatReply(msg.content)}</div>`);
        const cards = msg.role === "assistant" ? cleanCards(msg.cards) : undefined;
        if (cards) parts.push(`<div class="qb-chatbot__cards">${cards.map(cardHtml).join("")}</div>`);
        const actions = msg.role === "assistant" ? cleanActions(msg.actions) : undefined;
        if (actions) {
          parts.push(
            `<div class="qb-chatbot__chips">${actions
              .map((a) => `<button class="qb-chatbot__chip" type="button" data-qb-tab-action="${escapeHtml(a.tab)}">${escapeHtml(a.label)}</button>`)
              .join("")}</div>`
          );
        }
      }

      if (this.sending) {
        parts.push(`
          <div class="qb-chatbot__msg qb-chatbot__msg--bot qb-chatbot__typing" aria-label="${this.toolActive ? "Checking live stock" : "Assistant is typing"}">
            <span></span><span></span><span></span>${this.toolActive ? '<em class="qb-chatbot__tool-note">checking products…</em>' : ""}
          </div>
        `);
      }

      if (this.humanMode) {
        parts.push(
          `<p class="qb-chatbot__mode">You're chatting with the store team now - their replies show up here and in your email.</p>`
        );
      }

      this.threadEl.innerHTML = parts.join("");
      this.threadEl.scrollTop = this.threadEl.scrollHeight;

      // Already talking to a human — the handoff button would be noise.
      const humanBtn = this.panel.querySelector("[data-qb-escalate-toggle]");
      if (humanBtn) humanBtn.hidden = this.humanMode;

      const errorEl = this.panel.querySelector("[data-qb-chat-error]");
      if (errorEl) {
        errorEl.hidden = !this.error;
        errorEl.textContent = this.error || "";
      }
    }

    renderEscalate() {
      const box = this.panel.querySelector("[data-qb-escalate-box]");
      if (!box) return;
      box.hidden = !this.escalateOpen;
      if (!this.escalateOpen) return;

      if (this.escalateDone) {
        box.innerHTML = this.humanMode
          ? `<p class="qb-chatbot__escalate-done">Thanks - the team has your message. Their replies will show up right here, and in your email.</p>`
          : `<p class="qb-chatbot__escalate-done">Thanks — your message is on its way to the team. They'll reply by email.</p>`;
        return;
      }

      const email = window.QB?.customer?.loggedIn ? window.QB.customer.email : "";
      box.innerHTML = `
        <p class="qb-chatbot__escalate-title">Leave a message for the team</p>
        <input class="qb-chatbot__escalate-input" data-qb-escalate-email type="email" placeholder="Your email" value="${escapeHtml(email || "")}" aria-label="Your email" />
        <textarea class="qb-chatbot__escalate-input" data-qb-escalate-msg rows="3" maxlength="2000" placeholder="How can we help?" aria-label="Message for the team"></textarea>
        <button class="qb-chatbot__escalate-send" type="button" data-qb-escalate-send>Send to the team</button>
      `;
    }

    bind() {
      this.panel.addEventListener("click", async (e) => {
        const add = e.target.closest("[data-qb-add]");
        if (add) {
          addToCart(add);
          return;
        }
        const tabAction = e.target.closest("[data-qb-tab-action]");
        if (tabAction) {
          window.QB?.drawer?.setActiveTab?.(tabAction.dataset.qbTabAction);
          return;
        }
        const chip = e.target.closest("[data-qb-chip]");
        if (chip) {
          this.send(chip.textContent.trim());
          return;
        }
        if (e.target.closest("[data-qb-chat-send]")) {
          this.send(this.inputEl.value);
          return;
        }
        if (e.target.closest("[data-qb-escalate-toggle]")) {
          // Shopify Inbox as the human help: open it, or show the form when it
          // is not on the page.
          if (!this.escalateOpen && window.QB?.help?.mode === "inbox" && openInbox()) return;
          this.escalateOpen = !this.escalateOpen;
          this.renderEscalate();
          return;
        }
        if (e.target.closest("[data-qb-escalate-send]")) {
          await this.escalate();
        }
      });

      this.panel.addEventListener("keydown", (e) => {
        if (e.target === this.inputEl && e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          this.send(this.inputEl.value);
        }
      });
    }

    async send(raw) {
      const message = String(raw || "").trim();
      if (!message || this.sending || this.streaming || !this.workerUrl) return;

      this.messages.push({ role: "user", content: message });
      this.inputEl.value = "";
      this.sending = true;
      this.error = null;
      this.renderThread();

      try {
        const customer = window.QB?.customer;
        const post = async () => {
          const headers = await this.authHeaders();
          return qbFetch(`${this.workerUrl}/chat`, {
            method: "POST",
            headers,
            body: JSON.stringify({
              message,
              conversationId: this.conversationId,
              customerId: this.token ? customer.id : undefined,
              firstName: customer?.loggedIn ? customer.firstName : undefined,
              // The storefront's market country, for prices in the shopper's
              // currency (worker v0.34.0 checks it against the store's markets).
              country: typeof window.Shopify?.country === "string" ? window.Shopify.country : undefined,
              // The product or collection page the shopper is on (worker
              // v0.35.0, plan WP7), so "does this come in a 10?" just works.
              page: pageRef(),
              stream: true,
              // Stateless fallback for Workers without a D1 binding
              history: this.messages.slice(0, -1).slice(-20),
            }),
          });
        };
        const res = await this.recoverBoundConversation(await post(), post, { keepLast: true });

        if (res.ok && (res.headers.get("Content-Type") || "").includes("text/event-stream")) {
          await this.consumeStream(res);
        } else {
          const data = await res.json();
          if (!res.ok) throw new Error(data.error || "chat_failed");
          this.conversationId = data.conversationId || this.conversationId;
          if (data.mode === "human") {
            // The store team has this thread — the message went to their
            // ticket and there is no assistant reply to render.
            this.setHumanMode(true);
          } else {
            this.messages.push({ role: "assistant", content: data.reply, actions: cleanActions(data.actions), cards: cleanCards(data.cards) });
          }
          if (data.lastId) this.lastId = data.lastId;
        }
        saveSession(this.conversationId, this.messages, this.lastId, this.humanMode);
      } catch (err) {
        const messages = {
          rate_limited: "You're sending messages a little fast — give it a moment and try again.",
          message_too_long: "That message is a bit long — try shortening it.",
          chat_unavailable: "The assistant is having trouble right now. Try again shortly, or use \"Need a human?\".",
          chat_paused: "The assistant is taking a short break. Use \"Need a human?\" and the team will get back to you.",
          human_unavailable: "Couldn't reach the store team just now - give it a moment and send that again.",
        };
        this.error = messages[err.message] || "Something went wrong — try again, or use \"Need a human?\".";
      } finally {
        this.sending = false;
        this.toolActive = false;
        this.renderThread();
      }
    }

    // Reads the Worker's SSE stream: typing dots until the first token, then
    // the reply grows in place.
    async consumeStream(res) {
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let started = false;
      let gotError = false;
      this.streaming = true;

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          let idx;
          while ((idx = buffer.indexOf("\n\n")) !== -1) {
            const raw = buffer.slice(0, idx);
            buffer = buffer.slice(idx + 2);

            for (const line of raw.split("\n")) {
              if (!line.startsWith("data:")) continue;
              let evt;
              try {
                evt = JSON.parse(line.slice(5).trim());
              } catch {
                continue;
              }
              if (evt.type === "start") {
                this.conversationId = evt.conversationId || this.conversationId;
              } else if (evt.type === "delta") {
                if (!started) {
                  started = true;
                  this.sending = false;
                  this.messages.push({ role: "assistant", content: "" });
                } else if (this.sending) {
                  // Text resuming after a tool round — drop the indicator.
                  this.sending = false;
                }
                this.toolActive = false;
                this.messages[this.messages.length - 1].content += evt.text;
                this.renderThread();
              } else if (evt.type === "tool") {
                // The Worker is running a live product lookup between model
                // turns — show the typing dots with a stock-check note.
                this.toolActive = true;
                this.sending = true;
                this.renderThread();
              } else if (evt.type === "done") {
                this.conversationId = evt.conversationId || this.conversationId;
                if (evt.lastId) this.lastId = evt.lastId;
                const actions = started ? cleanActions(evt.actions) : undefined;
                const cards = started ? cleanCards(evt.cards) : undefined;
                if (actions || cards) {
                  const last = this.messages[this.messages.length - 1];
                  if (actions) last.actions = actions;
                  if (cards) last.cards = cards;
                  this.renderThread();
                }
              } else if (evt.type === "error") {
                gotError = true;
              }
            }
          }
        }
      } finally {
        this.streaming = false;
      }

      if (gotError && !started) throw new Error("chat_unavailable");
      if (gotError && started) {
        this.error = "That reply may have been cut off — ask again if something's missing.";
      }
    }

    async escalate() {
      const emailEl = this.panel.querySelector("[data-qb-escalate-email]");
      const msgEl = this.panel.querySelector("[data-qb-escalate-msg]");
      const email = emailEl?.value.trim() || "";
      const message = msgEl?.value.trim() || "";
      if (!email || !message || !this.workerUrl) return;

      try {
        const post = async () => {
          const headers = await this.authHeaders();
          return qbFetch(`${this.workerUrl}/chat/escalate`, {
            method: "POST",
            headers,
            body: JSON.stringify({
              email,
              message,
              conversationId: this.conversationId,
              customerId: this.token ? window.QB.customer.id : undefined,
            }),
          });
        };
        const res = await this.recoverBoundConversation(await post(), post);
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "escalate_failed");
        // Signed in on a thread started as a guest: the worker moved the
        // escalation onto a new conversation bound to this customer.
        if (data.conversationId && data.conversationId !== this.conversationId) {
          this.conversationId = data.conversationId;
          this.lastId = 0;
        }
        this.escalateDone = true;
        // A Gorgias ticket opened: the thread is the team's now, and their
        // replies will arrive through the poll.
        if (data.mode === "human") {
          this.escalateOpen = true; // keep the confirmation visible
          this.humanMode = true;
          saveSession(this.conversationId, this.messages, this.lastId, this.humanMode);
          this.renderThread();
        }
      } catch {
        this.error = "Couldn't send your message — check the email address and try again.";
        this.renderThread();
      }
      this.renderEscalate();
    }
  }

  class QBChatEmbedSlot {
    constructor(panel, config) {
      this.panel = panel;
      this.config = config;
      this.loaded = false;
      this.render();
    }

    render() {
      const { embedScript } = this.config;

      if (!embedScript || !isValidScriptUrl(embedScript)) {
        this.panel.innerHTML = `
          <div class="qb-chat qb-chat--empty">
            <p class="qb-chat__message">Chat is not configured for this store yet.</p>
            <p class="qb-chat__fine">Set <code>QB_SLOTS.chat.embedScript</code> to your provider URL, or <code>mode: "concierge"</code> for the AI assistant.</p>
          </div>
        `;
        return;
      }

      this.panel.innerHTML = `
        <div class="qb-chat">
          <div class="qb-chat__mount" data-qb-chat-mount aria-live="polite">
            <p class="qb-chat__loading">Loading chat…</p>
          </div>
        </div>
      `;
    }

    onActivate() {
      if (this.loaded) return;
      const { provider, embedScript, hideSelectors } = this.config;

      if (!embedScript || !isValidScriptUrl(embedScript)) return;

      hideNativeBubbles(provider, hideSelectors);

      const mount = this.panel.querySelector("[data-qb-chat-mount]");
      if (!mount) return;

      const script = document.createElement("script");
      script.src = embedScript;
      script.async = true;
      script.onload = () => {
        const loading = mount.querySelector(".qb-chat__loading");
        if (loading) loading.remove();
        this.loaded = true;
      };
      script.onerror = () => {
        mount.innerHTML = `<p class="qb-chat__error" role="alert">Could not load chat. Please try again later.</p>`;
      };

      mount.appendChild(script);
      this.loaded = true;
    }
  }

  window.QB = window.QB || {};
  // For qb-drawer.js, which applies the button policy when /config lands.
  window.QB.inbox = { open: openInbox, applyButtonPolicy: applyInboxButtonPolicy };
  if (window.QB.help) applyInboxButtonPolicy(window.QB.help);
  window.QB.slotModules = window.QB.slotModules || {};
  window.QB.slotModules.chat = {
    version: VERSION,
    init(panel) {
      const config = window.QB?.slots?.chat || {};
      const mode =
        config.mode ||
        (config.embedScript ? "embed" : window.QB?.worker?.url ? "concierge" : "embed");
      return mode === "concierge"
        ? new QBConciergeSlot(panel, config)
        : new QBChatEmbedSlot(panel, config);
    },
  };
})();
